import React, { useState } from 'react';
import { Send, CheckCircle, User, Mail, Phone, MapPin, MessageSquare, DollarSign, Briefcase, Sparkles, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Marquee from '../components/Marquee';
import StarBadge from '../components/StarBadge';

const Franchise = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    investmentBudget: '',
    experience: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Franchise inquiry submitted:', formData);
    setIsLoading(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-28 bg-rush-cream">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-xl relative">
            <div className="absolute -top-6 -right-6">
              <StarBadge text="SENT!" color="bg-rush-mint" textColor="text-rush-navy" size="lg" />
            </div>
            <div className="w-24 h-24 bg-rush-mint/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-rush-navy" size={48} />
            </div>
            <h2 className="text-4xl font-display text-rush-navy mb-4">
              thank you!
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Your franchise inquiry has been received. Our team will review your application and get back to you within 3-5 business days. 🚀
            </p>
            <Button 
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  country: '',
                  city: '',
                  investmentBudget: '',
                  experience: '',
                  message: ''
                });
              }}
              className="bg-rush-blue hover:bg-rush-blue/90 text-white font-bold rounded-full px-8"
            >
              Submit Another Inquiry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 bg-rush-cream">
      {/* Hero Section */}
      <section className="py-16 bg-rush-navy relative overflow-hidden">
        <div className="absolute top-10 left-10 w-48 h-48 bg-rush-pink/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-rush-blue/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="inline-flex items-center bg-rush-mint/20 text-rush-mint rounded-full px-4 py-2 mb-6">
            <Sparkles size={16} className="mr-2" />
            <span className="text-sm font-semibold">Franchise Opportunities</span>
          </span>
          <h1 className="text-5xl md:text-6xl font-display text-white mb-6">
            join the<br />
            <span className="text-rush-pink">rush family!</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Become a franchise partner and bring the joy of premium frozen yogurt to your community. 🍦
          </p>
        </div>
      </section>

      {/* Marquee */}
      <Marquee text="PARTNER WITH US" bgColor="bg-rush-pink" />

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: '📈', title: 'Proven Business Model', desc: 'Benefit from our established brand and operational expertise.' },
              { icon: '💰', title: 'Attractive Returns', desc: 'Join a growing market with strong profit potential.' },
              { icon: '🤝', title: 'Full Support', desc: 'Training, marketing, and ongoing operational support.' }
            ].map((benefit, idx) => (
              <div key={idx} className="text-center p-8 bg-rush-cream rounded-3xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <span className="text-5xl mb-4 block">{benefit.icon}</span>
                <h3 className="text-xl font-bold text-rush-navy mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-rush-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl relative">
            <div className="absolute -top-6 -left-6">
              <StarBadge text="LET'S TALK" color="bg-rush-blue" size="lg" />
            </div>
            
            <h2 className="text-3xl font-display text-rush-navy mb-2 text-center">
              franchise inquiry
            </h2>
            <p className="text-gray-600 text-center mb-10">
              Fill out the form below and our team will contact you shortly. 📩
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700 font-medium flex items-center gap-2">
                    <User size={16} className="text-rush-blue" />
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="rounded-xl border-gray-200 focus:border-rush-blue focus:ring-rush-blue"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium flex items-center gap-2">
                    <Mail size={16} className="text-rush-blue" />
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="rounded-xl border-gray-200 focus:border-rush-blue focus:ring-rush-blue"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700 font-medium flex items-center gap-2">
                    <Phone size={16} className="text-rush-blue" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+234 801 234 5678"
                    required
                    className="rounded-xl border-gray-200 focus:border-rush-blue focus:ring-rush-blue"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country" className="text-gray-700 font-medium flex items-center gap-2">
                    <MapPin size={16} className="text-rush-blue" />
                    Preferred Country *
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('country', value)} required>
                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-rush-blue focus:ring-rush-blue">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nigeria">🇳🇬 Nigeria</SelectItem>
                      <SelectItem value="ghana">🇬🇭 Ghana</SelectItem>
                      <SelectItem value="lithuania">🇱🇹 Lithuania</SelectItem>
                      <SelectItem value="other">🌍 Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city" className="text-gray-700 font-medium">
                  Preferred City/Location
                </Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Lagos, Accra, Vilnius, etc."
                  className="rounded-xl border-gray-200 focus:border-rush-blue focus:ring-rush-blue"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="investmentBudget" className="text-gray-700 font-medium flex items-center gap-2">
                    <DollarSign size={16} className="text-rush-blue" />
                    Investment Budget
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('investmentBudget', value)}>
                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-rush-blue focus:ring-rush-blue">
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-50k">Under $50,000</SelectItem>
                      <SelectItem value="50k-100k">$50,000 - $100,000</SelectItem>
                      <SelectItem value="100k-200k">$100,000 - $200,000</SelectItem>
                      <SelectItem value="over-200k">Over $200,000</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="experience" className="text-gray-700 font-medium flex items-center gap-2">
                    <Briefcase size={16} className="text-rush-blue" />
                    Business Experience
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('experience', value)}>
                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-rush-blue focus:ring-rush-blue">
                      <SelectValue placeholder="Select experience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No prior experience</SelectItem>
                      <SelectItem value="some">Some business experience</SelectItem>
                      <SelectItem value="food-industry">Food industry experience</SelectItem>
                      <SelectItem value="franchise">Previous franchise owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-700 font-medium flex items-center gap-2">
                  <MessageSquare size={16} className="text-rush-blue" />
                  Additional Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us why you're interested in joining RUSH..."
                  rows={4}
                  className="rounded-xl border-gray-200 focus:border-rush-blue focus:ring-rush-blue resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-rush-pink hover:bg-rush-pink/90 text-white font-bold py-6 text-lg rounded-full"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Submit Inquiry
                    <Send className="ml-2" size={20} />
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Franchise;
